import React from 'react';
import { useTranslation } from 'react-i18next';
import { Race, RaceStatus } from './hooks';

interface Props {
    races: Race[];
    deletingId: string | null;
    onEdit: (race: Race) => void;
    onDelete: (id: string) => void;
}

const STATUS_STYLES: Record<RaceStatus, string> = {
    planned: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    registered: 'bg-blue-100 text-blue-800 border-blue-200',
    confirmed: 'bg-green-100 text-green-800 border-green-200',
    completed: 'bg-purple-100 text-purple-800 border-purple-200',
    cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const UpcomingTable: React.FC<Props> = ({ races, deletingId, onEdit, onDelete }) => {
    const { t } = useTranslation();

    const STATUS_LABELS: Record<RaceStatus, string> = {
        planned: t('races.statusPlanned'),
        registered: t('races.statusRegistered'),
        confirmed: t('races.statusConfirmed'),
        completed: t('races.statusCompleted'),
        cancelled: t('races.statusCancelled'),
    };
    const formatDate = (d: string) =>
        new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });

    if (races.length === 0) {
        return (
            <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-slate-400 text-sm italic">
                    {t('races.emptyUpcoming')}
                </td>
            </tr>
        );
    }

    return (
        <>
            {races.map((race, idx) => (
                <tr key={race.id} className="group hover:bg-cyan-50 transition-colors">
                    <td className="px-6 py-4 text-center font-mono text-slate-400 text-xs">
                        {String(idx + 1).padStart(2, '0')}
                    </td>
                    <td className="px-6 py-4">
                        <span className="font-bold text-slate-700">{race.raceType}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-700">{formatDate(race.date)}</td>
                    <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-slate-700">
                            <i className="fa-solid fa-location-dot text-slate-400 text-xs"></i>
                            {race.location}
                        </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700 text-sm">{race.notes ?? '—'}</td>
                    <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[race.status]}`}>
                            {STATUS_LABELS[race.status]}
                        </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={() => onEdit(race)}
                                className="w-8 h-8 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center"
                                title={t('common.edit')}
                            >
                                <i className="fa-solid fa-pen-to-square text-xs"></i>
                            </button>
                            <button
                                onClick={() => onDelete(race.id)}
                                disabled={deletingId === race.id}
                                className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors flex items-center justify-center disabled:opacity-40"
                                title={t('common.delete')}
                            >
                                {deletingId === race.id
                                    ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                    : <i className="fa-solid fa-trash-can text-xs"></i>
                                }
                            </button>
                        </div>
                    </td>
                </tr>
            ))}
        </>
    );
};

export default UpcomingTable;
